import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { companiesQueryKeys } from '@/modules/companies/queries/queryKeys';
import { useSourceJobMutation } from '@/modules/jobs/mutations/useSourceJobMutation';
import { useUpdateJobMutation } from '@/modules/jobs/mutations/useUpdateJobMutation';
import { jobsQueryKeys } from '@/modules/jobs/queries/queryKeys';
import {
  type JobResponseBody,
  type SubmitIntent,
  toJobUpdateRequest,
} from '@/modules/jobs/signal/jobRequestMappers';
import type {
  InboundDraft,
  RoleDraft,
  WorkflowDraft,
} from '@/modules/jobs/signal/types';
import { actionErrorMessage, errorHint } from '@/utils/actionErrorMessage';

/**
 * The panel→edit wiring shared by the recruiter (`JobEdit`) and customer (`HMJobEdit`) edit pages (plan 13b/13g):
 * PUT with the echo-back mapper; `publish` (offered only for Drafts) then kicks sourcing via `POST /source` —
 * PUT alone never flips `sourcingState` nor starts a search. Success navigates away, so it toasts; a failure keeps
 * the user on the form, so it comes back as `error` for the editor to show inline. Navigation is the page's, via
 * `onSaved`.
 */
export const useJobEditSubmit = (
  job: JobResponseBody | undefined,
  onSaved: (intent: SubmitIntent) => void,
) => {
  const updateJobMutation = useUpdateJobMutation();
  const sourceJobMutation = useSourceJobMutation();
  const queryClient = useQueryClient();
  // Tagged with its job: the page stays mounted when the route moves to another job's edit URL, and job B must not
  // show job A's failure.
  const [failure, setFailure] = useState<{
    jobId: string;
    message: string;
  } | null>(null);
  const setError = (message: string | null) =>
    setFailure(message && job ? { jobId: job.id, message } : null);

  const onSubmit = (
    role: RoleDraft,
    workflow: WorkflowDraft,
    intent: SubmitIntent,
    inbound?: InboundDraft,
  ) => {
    if (!job) return;
    setError(null);
    updateJobMutation.mutate(
      {
        jobId: job.id,
        body: { ...toJobUpdateRequest(role, workflow, job), ...inbound },
      },
      {
        onSuccess: () => {
          if (inbound) {
            void queryClient.invalidateQueries({
              queryKey: jobsQueryKeys.inbound(job.id),
            });
            // The Workspace's Careers Page section counts the jobs that accept applications.
            void queryClient.invalidateQueries({
              queryKey: companiesQueryKeys.inbound(workflow.companyId),
            });
          }
          if (intent === 'publish') {
            sourceJobMutation.mutate(
              { jobId: job.id },
              {
                onSuccess: () => {
                  // /source commits Draft -> New before the worker later moves it to InProgress. Keep the cached
                  // detail on the intended active state while that handoff completes.
                  queryClient.setQueryData<JobResponseBody>(
                    jobsQueryKeys.detail(job.id),
                    (old) =>
                      old ? { ...old, sourcingState: 'InProgress' } : old,
                  );
                  toast.success(
                    'Job published. Finlay started looking for candidates.',
                  );
                  onSaved(intent);
                },
                onError: (sourceError) =>
                  setError(
                    `Your changes are saved, but the search didn't start. ${errorHint(sourceError)}`,
                  ),
              },
            );
            return;
          }
          toast.success('Job saved');
          onSaved(intent);
        },
        onError: (updateError) =>
          setError(actionErrorMessage('save the job', updateError)),
      },
    );
  };

  return {
    onSubmit,
    submitting: updateJobMutation.isPending || sourceJobMutation.isPending,
    error: failure && failure.jobId === job?.id ? failure.message : null,
  };
};
